import {
  Menu,
  MenuItem,
  MenuSeparator,
} from '@/components/interactives/dropdown-menu.tsx'
import {
  faArrowUpRightFromSquare,
  faAt,
  faBackwardStep,
  faChevronDown,
  faCog,
  faDisplay,
  faDollarSign,
  faFileCsv,
  faFileExport,
  faFilePdf,
  faFolder,
  faHome,
  faLink,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons'
import { useNavigate } from 'react-router-dom'
import {
  generateCsv,
  unsetRundownRunner,
  updateRundown,
} from '../../../firestore'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { rundownAtom } from '@/store/rundown.store'
import { toastAtom } from '@/store/toast.store'
import { planAtom } from '@/store/plan.store'

import { format } from 'date-fns'
import { isValueInEnum } from '../../../utils/primitives'
import { useRundownAccess } from '../../permissions/lib/use-rundown-access'
import { RundownPermission, Features } from '@rundown-studio/types'
import { modalActions } from '../modals/modal.store'

type Props = {
  teamId: string
  eventId: string
  eventUrl: string
  onOpenSettings: () => void
  onInviteGuests: () => void
  onGenerateOutput: () => void
  onExportPdf: () => void
  onExportCsv: () => void
  onShowTrash: () => void
  openUpgradeModal: () => void
}

export function RundownNavbarMenu({
  teamId,
  eventId,
  eventUrl,
  onOpenSettings,
  onInviteGuests,
  onGenerateOutput,
  onExportPdf,
  onShowTrash,
  openUpgradeModal,
}: Props) {
  const navigate = useNavigate()

  const [rundown, setRundown] = useAtom(rundownAtom)
  const plan = useAtomValue(planAtom)

  const { hasPermission } = useRundownAccess()
  const canEdit = hasPermission(RundownPermission.EDIT)

  const addToast = useSetAtom(toastAtom)

  function handleResetRunner() {
    if (!rundown) return

    updateRundown(rundown.id, { startCueId: null }).then(({ data }) =>
      setRundown(data),
    )

    unsetRundownRunner(rundown.id)
  }

  async function handleDownloadCsv() {
    if (!rundown || !plan) return

    // User is on a plan which does not include exporting
    if (!isValueInEnum(Features.EXPORT, plan.features)) {
      return openUpgradeModal()
    }

    try {
      const response = await generateCsv(rundown.id)

      // Create blob with csv
      const blob = new Blob([response.data], { type: 'text/csv' })

      // Create a with content to download csv
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.rel = 'noreferrer'
      link.download = `${rundown.name}-${format(Date.now(), 'yyyyMMdd-HHmm')}.csv`
      link.click()
    } catch (error) {
      addToast({ title: 'Error downloading the CSV', type: 'fail' })
      console.error('Error downloading the CSV:', error)
    }
  }

  return (
    <Menu label="Rundown" icon={faChevronDown}>
      <MenuItem
        icon={faHome}
        label="Dashboard"
        onClick={() => navigate(`/dashboard/${teamId}`)}
      />

      {eventId && eventUrl && (
        <MenuItem
          icon={faFolder}
          label="Event"
          onClick={() => navigate(eventUrl)}
          disabled={!canEdit}
        />
      )}

      <MenuSeparator />

      <MenuItem
        icon={faCog}
        label="Settings"
        onClick={onOpenSettings}
        disabled={!canEdit}
      />

      <MenuItem
        icon={faAt}
        label="Mentions"
        onClick={() => modalActions.open('MENTIONS', {})}
        disabled={!canEdit}
      />

      <MenuItem
        icon={faDollarSign}
        label="Text Variables"
        onClick={() => modalActions.open('VARIABLES', {})}
        disabled={!canEdit}
      />

      <MenuSeparator />

      <MenuItem
        icon={faLink}
        label="Invite Guests"
        onClick={onInviteGuests}
        disabled={!canEdit}
      />

      <MenuItem
        icon={faDisplay}
        label="Generate Output"
        onClick={onGenerateOutput}
        disabled={!canEdit}
      />

      <Menu label="Export as..." icon={faFileExport} disabled={!canEdit}>
        <MenuItem icon={faFilePdf} label="PDF" onClick={onExportPdf} />
        <MenuItem icon={faFileCsv} label="CSV" onClick={handleDownloadCsv} />
      </Menu>

      <MenuSeparator />

      <MenuItem
        icon={faArrowUpRightFromSquare}
        label="API Docs"
        onClick={() =>
          window.open(
            'https://rundownstudio.app/docs/rundown/api/',
            '_blank',
            'noreferrer',
          )
        }
        disabled={!canEdit}
      />

      <MenuSeparator />

      <MenuItem
        icon={faTrashAlt}
        label="Trash"
        onClick={onShowTrash}
        disabled={!canEdit}
      />

      <MenuSeparator />

      <MenuItem
        icon={faBackwardStep}
        label="Reset rundown timing"
        onClick={handleResetRunner}
        disabled={!canEdit}
      />
    </Menu>
  )
}
